import {Component, ConfigOptions} from 'panel';
import {registerMPElement} from 'common/util/register-element';

import template from './index.jade';
import css from './index.styl?inline';
import {createElem} from 'common/util/dom';

interface InputState {
  value: string;
}

export enum InputType {
  Text = `text`,
  Password = `password`,
  Number = `number`,
}

export enum Necessary {
  Default = ``,
  Optional = `optional`,
  Required = `required`,
}

export enum ValidationStatus {
  Default = ``,
  Valid = `valid`,
  Invalid = `invalid`,
}

class InputComponent extends Component<InputState> {
  static useShadowDom = true;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      disabled: {type: `boolean`, description: `Whether input is disabled`},
      'field-id': {type: `string`, description: `Id of input field, maps to field label for accessibility`},
      'field-name': {type: `string`, description: `Name of the input field, maps to field label`},
      label: {type: `string`, default: ``, description: `Label on top that describes the input field`},
      placeholder: {type: `string`, description: `Placeholder text for input`},
      type: {type: `string`, enum: Object.values(InputType), default: InputType.Text},
      necessary: {type: `string`, enum: Object.values(Necessary), default: Necessary.Default},
      'validation-status': {type: `string`, enum: Object.values(ValidationStatus), default: ValidationStatus.Default},
      'error-message': {type: `string`, description: `Error message to show when invalid validation status`},
      maxlength: {type: `number`, description: `Maximum number of characters allowed`, default: Necessary.Optional},
    };
  }

  private searchInput: HTMLInputElement;

  get config(): ConfigOptions<InputState> {
    // @ts-ignore - useShadowDom is a static var
    const useShadowDom = this.constructor.useShadowDom;
    return {
      css: useShadowDom ? css : null,
      template,
      useShadowDom,
      defaultState: {
        value: ``,
      },
      helpers: {
        handleInput: (ev) => {
          this.update({value: ev.target.value});
        },
      },
    };
  }

  get value() {
    return this.state.value;
  }

  set value(value: string) {
    this.update({value});
  }

  focus() {
    if (this.searchInput) {
      this.searchInput.focus();
    } else {
      // try again after a frame if not ready
      requestAnimationFrame(() => this.focus());
    }
  }

  setElmRef(elm: HTMLInputElement) {
    this.searchInput = elm;
  }
}

class ShadowlessInput extends InputComponent {
  static useShadowDom = false;

  connectedCallback() {
    super.connectedCallback();

    // add mp-input styles as global styles if not already added
    if (!document.head.querySelector(`style#mp-input-css`)) {
      document.head.appendChild(createElem(`style`, {type: `text/css`}, css.toString()));
    }
  }
}

registerMPElement(`mp-shadowless-input`, ShadowlessInput);
export default registerMPElement(`mp-input`, InputComponent);
